import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { RouteDailySnapshot } from '../../../domain/models/contracts';
import { TREND_WINDOW_DAYS, trailingHistory } from './routeMetrics';
import { CHART_COLORS, TOOLTIP_STYLE } from './chartTheme';
import { formatPct, formatUsd, formatUsdCompact } from './formatters';

interface PerformanceTrendChartProps {
  readonly history: readonly RouteDailySnapshot[];
}

export function PerformanceTrendChart({ history }: PerformanceTrendChartProps) {
  const series = useMemo(() => [...trailingHistory(history, TREND_WINDOW_DAYS)], [history]);

  return (
    <section className="ri-panel" aria-label="Historical route performance">
      <header className="ri-panel__header">
        <h2 className="ri-panel__title">Route Performance Trend</h2>
        <p className="ri-panel__subtitle">
          Daily net profit and load factor, last {TREND_WINDOW_DAYS} simulated days
        </p>
      </header>

      {series.length === 0 ? (
        <p className="ri-empty">No history yet. Let the simulation run for a day.</p>
      ) : (
        <div className="ri-chart" role="img" aria-label="Combined chart of daily net profit and load factor">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={series} margin={{ top: 12, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
              <XAxis
                dataKey="day"
                stroke={CHART_COLORS.axis}
                tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                tickLine={false}
                tickFormatter={(day: number) => `D${day}`}
                minTickGap={16}
              />
              <YAxis
                yAxisId="profit"
                orientation="left"
                stroke={CHART_COLORS.axis}
                tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                width={64}
                tickFormatter={(value: number) => formatUsdCompact(value)}
              />
              <YAxis
                yAxisId="loadFactor"
                orientation="right"
                domain={[0, 100]}
                stroke={CHART_COLORS.axis}
                tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                width={44}
                tickFormatter={(value: number) => `${value}%`}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: CHART_COLORS.text }}
                itemStyle={{ color: CHART_COLORS.text }}
                labelFormatter={(day: unknown) => `Day ${String(day)}`}
                formatter={(value: unknown, name: unknown): [string, string] => {
                  const label = typeof name === 'string' ? name : '';
                  if (typeof value !== 'number') return [String(value), label];
                  return [label === 'Load factor' ? formatPct(value) : formatUsd(value), label];
                }}
              />
              <Legend wrapperStyle={{ color: CHART_COLORS.axis, fontSize: 12 }} />
              <ReferenceLine yAxisId="profit" y={0} stroke={CHART_COLORS.axis} strokeDasharray="4 4" />
              <Bar
                yAxisId="profit"
                dataKey="netProfitUSD"
                name="Net profit"
                isAnimationActive={false}
                radius={[3, 3, 0, 0]}
              >
                {series.map((point) => (
                  <Cell
                    key={point.day}
                    fill={point.netProfitUSD >= 0 ? CHART_COLORS.profitPositive : CHART_COLORS.profitNegative}
                    fillOpacity={0.85}
                  />
                ))}
              </Bar>
              <Line
                yAxisId="loadFactor"
                type="monotone"
                dataKey="loadFactorPct"
                name="Load factor"
                stroke={CHART_COLORS.loadFactor}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
